import { Button, Flex, Stack, Switch, Text } from '@mantine/core';
import pluralize from 'pluralize';
import { useCallback, useMemo, useState } from 'react';
import { NavLink } from 'react-router';
import { FileUploadInput, UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { useFileUploadActions } from 'src/data-capture/hooks/file-uploads/useFileUploadActions';
import { useFileUploads } from 'src/data-capture/hooks/file-uploads/useFileUploads';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import SelectableItemList from 'src/UI/drawer-flow/SelectableItemList';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { Client } from 'src/users';
import { useMeetingNote } from '../MeetingNoteProvider';
import { MeetingNoteData } from '../MeetingNoteFlow';
import { useGenerateMeetingNote } from '../hooks/useGenerateMeetingNote';

const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
];

const DOCUMENT_FILE_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt', '.md'];

const filterDocumentFiles = (files: UploadedFile[]): UploadedFile[] => {
  return files.filter(file => DOCUMENT_MIME_TYPES.includes(file.file.fileType));
};

export default function SelectAgendaStep(props: StepProps<MeetingNoteData>) {
  const { data, setData } = props;
  const { files, isLoading } = useFileUploads({
    clientId: data.client?.id,
  });
  const { uploadFiles } = useFileUploadActions({ clientId: data.client?.id });
  const { generateMeetingNote, isLoading: isGenerating } = useGenerateMeetingNote();
  const { flagEnabledMeetingNoteSummarisation } = useFlags();
  const [isUploading, setIsUploading] = useState(false);

  const handleSummariseToggle = (checked: boolean) => {
    setData({ ...data, summarise: checked });
  };

  const items = useMemo(() => {
    const documentFiles = filterDocumentFiles(files);

    return documentFiles.map(file => ({
      id: file.id,
      title: file.file.fileName,
      description: `Uploaded: ${new Date(file.file.createdAt).toLocaleDateString()}`,
    }));
  }, [files]);

  const setSelectedAgendaFileId = useCallback(
    (selectedAgendaFileId: string | undefined) => {
      setData({ ...data, selectedAgendaFileId });
    },
    [data, setData],
  );

  const handleSelectionChange = (selectedIds: string[]) => {
    setSelectedAgendaFileId(selectedIds[0] || undefined);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;

    if (fileList) {
      setIsUploading(true);
      try {
        const payload: FileUploadInput[] = Array.from(fileList).map(file => ({
          file,
          isResolved: true,
        }));
        const result = await uploadFiles(payload);
        const newUploadedFileId = result[0]?.data?.id;
        if (newUploadedFileId) {
          setSelectedAgendaFileId(newUploadedFileId);
        }
      } catch (error) {
        notifyAndLogError(
          'Failed to upload file',
          'An error occurred while uploading the agenda file. Please try again.',
          error,
        );
      }
      setIsUploading(false);
    }

    event.target.value = '';
  };

  const handleGenerateMeetingNote = async () => {
    if (!props.data.client) {
      throw new Error('Client not found');
    }
    if (!props.data.selectedRecordingId) {
      throw new Error('Recording not selected');
    }
    if (!props.data.selectedAgendaFileId) {
      throw new Error('Agenda file not selected');
    }
    try {
      await generateMeetingNote(props.data.client.id, {
        dataUploadFileId: props.data.selectedRecordingId,
        agendaFileId: props.data.selectedAgendaFileId,
        summarise: props.data.summarise,
      });
      props.onSuccess();
    } catch (error) {
      notifyAndLogError('Error', 'Failed to generate meeting notes from agenda', error);
    }
  };

  const summariseToggle = flagEnabledMeetingNoteSummarisation && (
    <Flex justify="space-between" align="center">
      <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
        Summarise meeting note
      </Text>
      <Switch
        checked={data.summarise ?? true}
        onChange={event => handleSummariseToggle(event.currentTarget.checked)}
      />
    </Flex>
  );

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Select agenda"
      description="This agenda will be used as the template structure for the meeting note"
      isLoading={isLoading}
      continueText={isGenerating ? 'Generating...' : 'Generate meeting note'}
      canGoForward={!isGenerating && !!data.selectedAgendaFileId}
      canGoBack={!isGenerating}
      nextStep={handleGenerateMeetingNote}
      isSubmitting={isGenerating}
      footer={
        <Stack gap="xs">
          {summariseToggle}
          {items.length > 0 && (
            <Stack gap="xxs">
              <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.10">
                Can&apos;t find the agenda?
              </Text>
              <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.7">
                Upload the agenda to <DocumentLink client={props.data.client} /> or use the upload
                button above and it will appear here.
              </Text>
            </Stack>
          )}
        </Stack>
      }
    >
      <Flex direction="column" gap="xs" mih={0}>
        <Flex justify="space-between" align="center">
          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="black" data-testid="agenda-count">
            {items.length} {pluralize('document', items.length)} found
          </Text>
          <Button
            component="label"
            variant="subtle"
            loading={isUploading}
            style={{ cursor: 'pointer' }}
          >
            Upload agenda
            <input
              type="file"
              accept={DOCUMENT_FILE_EXTENSIONS.join(',')}
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
          </Button>
        </Flex>
        <SelectableItemList
          items={items}
          hint="The agenda structure will be used to generate the meeting note sections. Client information will be added as supplementary context."
          selectedItemIds={data.selectedAgendaFileId ? [data.selectedAgendaFileId] : []}
          setSelectedItemIds={handleSelectionChange}
          selectMultiple={false}
          emptyView={
            <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.10">
              Upload a meeting agenda (PDF, Word, or text file) using the button above or to{' '}
              <DocumentLink client={props.data.client} /> and it will appear here.
            </Text>
          }
        />
      </Flex>
    </DrawerFlowScreenLayout>
  );
}

function DocumentLink({ client }: { client: Client | undefined }) {
  const { close } = useMeetingNote();

  if (client == null) {
    return null;
  }

  return (
    <Text
      component={NavLink}
      to={`/client/${client.id}/document/add`}
      size="textXs"
      fw={FONT_WEIGHTS.medium}
      c="brand.8"
      onClick={close}
    >
      {client?.firstName}&apos;s documents
    </Text>
  );
}
